// Drilldown API
import api, { type AxiosResponse } from './client';
import type {
  DrilldownMetricConfig,
  DrilldownDetailsResponse,
  DrilldownBreakdownResponse,
  DrilldownSummaryResponse,
  DrilldownFilters,
} from './types';

/** CR-6192: one point of a per-period OTIF trend (from /drilldown/{metric}/trend/). */
export interface DrilldownTrendPoint {
  date: string;
  otif: number | null;
  total_lines: number;
  otif_lines: number;
  arrears: number;
}

export interface DrilldownTrendResponse {
  metric: string;
  period: string;
  series: DrilldownTrendPoint[];
}

export const drilldownAPI = {
  // Get available drill-down metrics configuration
  getConfig: (): Promise<AxiosResponse<DrilldownMetricConfig[]>> =>
    api.get('/drilldown/config/'),

  // CR-6192: per-period OTIF time-series for a PO-line metric (daily by default).
  // Shares scoping/exclusions with getDetails, so a chart point and its
  // drill-down show the same lines.
  getTrend: (
    metric: string,
    params?: DrilldownFilters & { period?: 'day' | 'week' | 'month' }
  ): Promise<AxiosResponse<DrilldownTrendResponse>> =>
    api.get(`/drilldown/${metric}/trend/`, { params }),

  // Get detailed records for a metric
  getDetails: (
    metric: string,
    params?: DrilldownFilters & { page?: number; page_size?: number; ordering?: string }
  ): Promise<AxiosResponse<DrilldownDetailsResponse>> =>
    api.get(`/drilldown/${metric}/details/`, { params }),

  // Get breakdown/grouped data for a metric
  getBreakdown: (
    metric: string,
    groupBy: string,
    filters?: DrilldownFilters
  ): Promise<AxiosResponse<DrilldownBreakdownResponse>> =>
    api.get(`/drilldown/${metric}/breakdown/`, { params: { group_by: groupBy, ...filters } }),

  // Get summary statistics for a metric
  getSummary: (
    metric: string,
    filters?: DrilldownFilters
  ): Promise<AxiosResponse<DrilldownSummaryResponse>> =>
    api.get(`/drilldown/${metric}/summary/`, { params: filters }),

  // Export drill-down data
  exportData: (
    metric: string,
    format: 'csv' | 'xlsx',
    filters?: DrilldownFilters
  ): Promise<AxiosResponse<Blob>> =>
    api.get(`/drilldown/${metric}/export/`, {
      params: { export_format: format, ...filters },
      responseType: 'blob',
    }),
};
